
import { useState, useEffect } from "react";
import { Calendar, User, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BlogPost } from "@/types/blogPost";
import { BlogContent } from "./BlogContent";
import { RelatedPostsCarousel } from "./RelatedPostsCarousel";
import { fetchRelatedBlogPosts } from "@/utils/blogOperations";

interface BlogPostDisplayProps {
  post: BlogPost;
  onBack: () => void;
}

export function BlogPostDisplay({ post, onBack }: BlogPostDisplayProps) {
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    const loadRelatedPosts = async () => {
      if (post.category_id) {
        try {
          const related = await fetchRelatedBlogPosts(post.id, post.category_id);
          setRelatedPosts(related);
        } catch (error) {
          console.error('Error loading related posts:', error);
        }
      }
    };

    loadRelatedPosts();
  }, [post.id, post.category_id]);

  const categoryInfo = (post as any).blog_categories;
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-8">
      <Button onClick={onBack} variant="outline" className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Voltar aos Artigos
      </Button>

      <article className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {post.title}
          </h1>
          {post.subtitle && (
            <h2 className="text-xl sm:text-2xl text-gray-600 mb-6">
              {post.subtitle}
            </h2>
          )}
          
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-gray-500" />
              <span className="text-sm text-gray-600">
                {post.created_at ? formatDate(post.created_at) : "Data não informada"}
              </span>
            </div>
            <div className="flex items-center">
              <User className="w-4 h-4 mr-2 text-gray-500" />
              <span className="text-sm text-gray-600">Dra Brenda Ramos</span>
            </div>
            {categoryInfo && (
              <Badge 
                style={{ 
                  backgroundColor: categoryInfo.color_hex,
                  borderColor: categoryInfo.border_color_hex,
                  color: '#000'
                }}
                className="border"
              >
                {categoryInfo.name}
              </Badge>
            )}
          </div>
        </header>

        {/* Featured Image */}
        {post.featured_image_url && (
          <div className="mb-8">
            <img
              src={post.featured_image_url}
              alt={post.title}
              className="w-full h-64 sm:h-96 object-cover rounded-lg"
            />
          </div>
        )}

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <BlogContent content={post.content} />
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <RelatedPostsCarousel posts={relatedPosts} />
      )}
    </div>
  );
}
